import { createFileRoute } from '@tanstack/react-router'
import { businessNumberLabel } from '@/lib/locale'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Printer, Lock } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/c/$token')({ component: PublicCertificate })

interface CertData {
  doc_number: string
  doc_type: string
  issued_at: string
  signed_at: string | null
  licence_number: string | null
  payload: Record<string, unknown> | null
  company: { name: string | null; logo: string | null; accent_color: string | null; abn: string | null; phone: string | null; email: string | null; country: string | null }
  client: { first_name: string | null; last_name: string | null; organization: string | null } | null
}

const fmtDate = (iso: string) =>
  phFormat(new Date(iso), 'date', { day: 'numeric', month: 'long', year: 'numeric' })
const isSignature = (v: unknown) => typeof v === 'string' && v.startsWith('data:image')

function PublicCertificate() {
  const { token } = Route.useParams()

  const q = useQuery({
    queryKey: ['public-certificate', token],
    queryFn: async (): Promise<CertData | null> => {
      const { data, error } = await supabase.rpc('public_get_compliance_doc', { p_token: token })
      if (error) throw error
      return data as CertData | null
    },
  })

  if (q.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-canvas-subtle"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>
  }
  if (!q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 text-center">
        <div>
          <p className="font-display text-[20px] font-bold text-token-primary">Certificate not found</p>
          <p className="mt-1 text-[14px] text-token-secondary">This link may have been withdrawn.</p>
        </div>
      </div>
    )
  }

  const d = q.data
  const accent = d.company.accent_color || '#0038a8'
  const coName = (d.company.name || 'The business').trim()
  const clientName = d.client
    ? [d.client.first_name, d.client.last_name].filter(Boolean).join(' ').trim() || d.client.organization
    : null
  const entries = Object.entries(d.payload ?? {})

  return (
    <div className="min-h-screen bg-canvas-subtle px-4 py-8 print:bg-token-surface print:py-0 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex justify-end print:hidden">
          <button
            onClick={() => window.print()}
            className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-token-subtle bg-token-surface px-3.5 text-[13px] font-semibold text-token-primary hover:bg-canvas-subtle"
          >
            <Printer className="h-4 w-4" /> Print / save as PDF
          </button>
        </div>

        <div className="rounded-[16px] border border-token-subtle bg-token-surface p-7 print:rounded-none print:border-0 print:p-0">
          <div className="flex items-start justify-between gap-6 border-b border-token-subtle pb-5">
            <div className="flex items-center gap-3">
              {d.company.logo
                ? <img src={d.company.logo} alt={coName} className="h-12 w-12 rounded-[10px] object-cover" />
                : <span className="flex h-12 w-12 items-center justify-center rounded-[10px] text-[20px] font-bold text-white" style={{ background: accent }}>{coName[0]?.toUpperCase()}</span>}
              <div>
                <p className="font-display text-[19px] font-bold tracking-[-0.02em] text-token-primary">{coName}</p>
                {d.company.abn && <p className="text-[12px] text-token-secondary">{businessNumberLabel(d.company)} {d.company.abn}</p>}
                {d.company.phone && <p className="text-[12px] text-token-secondary">{d.company.phone}</p>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">{d.doc_type}</p>
              <p className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{d.doc_number}</p>
              <p className="text-[12px] text-token-secondary">Issued {fmtDate(d.issued_at)}</p>
            </div>
          </div>

          {(clientName || d.licence_number) && (
            <div className="grid grid-cols-2 gap-6 border-b border-token-subtle py-5">
              {clientName && (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">Issued to</p>
                  <p className="mt-1 text-[14px] font-semibold text-token-primary">{clientName}</p>
                </div>
              )}
              {d.licence_number && (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.12em] text-token-secondary">Licence number</p>
                  <p className="mt-1 text-[14px] font-semibold text-token-primary">{d.licence_number}</p>
                </div>
              )}
            </div>
          )}

          <div className="py-5">
            {entries.length === 0 ? (
              <p className="text-[13px] text-token-secondary">This certificate has no recorded details.</p>
            ) : (
              <dl className="space-y-2.5">
                {entries.map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[38%_62%] gap-4 border-b border-token-subtle pb-2.5 last:border-0">
                    <dt className="text-[12.5px] text-token-secondary">{k}</dt>
                    <dd className="text-[13.5px] text-token-primary">
                      {isSignature(v) ? <img src={v as string} alt={`${k} signature`} className="h-16 object-contain" />
                        : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v ?? '—')}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          {d.signed_at && (
            <p className="flex items-center gap-1.5 border-t border-token-subtle pt-4 text-[12px] text-token-secondary">
              <Lock className="h-3.5 w-3.5 text-token-secondary" />
              Signed and locked {fmtDate(d.signed_at)}. This record cannot be altered; any correction is
              issued as a new certificate.
            </p>
          )}

          <p className="mt-4 text-[11px] leading-relaxed text-token-muted">
            {coName} is responsible for the accuracy of this certificate and for meeting the requirements of
            the relevant state or territory and its licence conditions.
            {d.company.email && <> Questions? <a className="font-semibold text-token-secondary" href={`mailto:${d.company.email}`}>{d.company.email}</a></>}
          </p>
        </div>
      </div>
    </div>
  )
}
